import { useState } from 'react';
import { Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDceProfiles, useForceProfilesApplicable } from '../../hooks/useDceAnalysis';
import type { DceAnalysis } from '../../types/dce-analysis.types';
import { ProfileCard } from './ProfileCard';

interface Props {
  analysis: DceAnalysis;
  opportunityId: number;
}

export function ProfilesSection({ analysis, opportunityId }: Props) {
  const enabled = analysis.profilesApplicable;
  const { data: profiles, isLoading } = useDceProfiles(enabled ? opportunityId : null);
  const forceMutation = useForceProfilesApplicable(opportunityId);
  const [confirmingForce, setConfirmingForce] = useState(false);

  const handleForce = () => {
    forceMutation.mutate(true, {
      onSuccess: () => {
        toast.success('Extraction profils activée. Relance l\'analyse pour les extraire.');
        setConfirmingForce(false);
      },
      onError: () => toast.error('Erreur'),
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          Profils consultants requis {profiles && `(${profiles.length})`}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!enabled && (
          <div className="rounded-md border bg-muted/40 p-4 text-sm">
            <div className="flex items-start gap-2 mb-2">
              <Info className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
              <div>
                <p className="font-medium">Pas de profils consultants identifiés pour ce type de marché</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Raison : <code className="text-[11px]">{analysis.profilesAppliedReason ?? 'default_fallback'}</code>
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Si l'acheteur a mal renseigné le CPV / type de procédure et que vous savez qu'un volet
                  équipe est attendu, vous pouvez forcer l'extraction (action tracée dans l'audit log).
                </p>
              </div>
            </div>
            {!confirmingForce ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmingForce(true)}
              >
                Forcer l'analyse profils
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleForce}
                  disabled={forceMutation.isPending}
                >
                  {forceMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                  Confirmer (puis relance l'analyse)
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmingForce(false)}
                >
                  Annuler
                </Button>
              </div>
            )}
          </div>
        )}

        {enabled && isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-copper" />
          </div>
        )}

        {enabled && !isLoading && (!profiles || profiles.length === 0) && (
          <p className="text-sm text-muted-foreground italic">
            Aucun profil consultant extrait. L'IA n'a peut-être pas identifié de section "équipe attendue" dans le CCTP.
          </p>
        )}

        {enabled && profiles && profiles.length > 0 && (
          <div className="space-y-3">
            {profiles.map((p) => (
              <ProfileCard key={p.id} profile={p} opportunityId={opportunityId} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
